import { api } from "../../lib/api";
import { apiClient } from "../../lib/apiClient";
import type {
  Organization,
  FacilityKpisResponse,
  FacilityOverviewResponse,
  SystemOverallEnergyResponse,
  SystemOverallUsageResponse,
  SystemComponentComparisonResponse,
  SystemStatusResponse,
  SystemComponentInstanceComparisonResponse,
  FacilityEnergyProfileResponse,
  SystemEnergyDistributionResponse,
  SystemEnergyPatternResponse,
  ResourceEnergyProfileResponse,
  AiInsightsResponse,
  CreateScenarioPayload,
  ScenarioResponse,
  PerformanceAnalysisPayload,
  PerformanceAnalysisResponse,
  SystemComponentTypesResponse,
  FacilitySummaryResponse,
  AdvancedEnergyAnalysisResponse,
  ExportExcelPayload,
} from "../types";

export const OrganizationService = {
  async fetchOrganizations(params?: {
    page?: number;
    page_size?: number;
    sort_dir?: string;
  }) {
    return await apiClient.get<Organization[]>("/api/v1/organizations", {
      params: { page: 1, page_size: 100, sort_dir: "asc", ...params },
    });
  },

  async fetchFacilityKpis(facilityId: number, params?: { period?: string }) {
    return await apiClient.get<FacilityKpisResponse>(
      `/api/v1/facilities/${facilityId}/kpis`,
      { params },
    );
  },

  async fetchFacilityOverview(facilityId: number) {
    return await apiClient.get<FacilityOverviewResponse>(
      `/api/v1/facilities/${facilityId}/overview`,
    );
  },

  async fetchSystemOverallLoad(
    facilityId: number,
    params?: { period?: string },
  ) {
    return await apiClient.get<SystemOverallEnergyResponse>(
      `/api/v1/facilities/${facilityId}/system-overall-load`,
      { params },
    );
  },

  async fetchSystemOverallGeneration(
    facilityId: number,
    params?: { period?: string },
  ) {
    return await apiClient.get<SystemOverallEnergyResponse>(
      `/api/v1/facilities/${facilityId}/system-overall-generation`,
      { params },
    );
  },

  async fetchSystemOverallUsage(
    facilityId: number,
    params?: { period?: string },
  ) {
    return await apiClient.get<SystemOverallUsageResponse>(
      `/api/v1/facilities/${facilityId}/system-overall-usage`,
      { params },
    );
  },

  async fetchSystemComponentComparison(
    facilityId: number,
    params?: { components?: string; date?: string },
  ) {
    return await apiClient.get<SystemComponentComparisonResponse>(
      `/api/v1/facilities/${facilityId}/system-component-comparison`,
      { params },
    );
  },

  async fetchSystemStatus(facilityId: number, status?: string) {
    // Only append the query string if status exists and is not "all"
    const query =
      status && status !== "all" ? `?status=${status.toLowerCase()}` : "";

    return await apiClient.get<SystemStatusResponse>(
      `/api/v1/facilities/${facilityId}/system-status${query}`,
    );
  },

  async fetchSystemComponentInstanceComparison(
    facilityId: number,
    params?: {
      component_type?: string;
      period?: string;
      start_date?: string;
      end_date?: string;
    },
  ) {
    return await apiClient.get<SystemComponentInstanceComparisonResponse>(
      `/api/v1/facilities/${facilityId}/system-component-instance-comparison`,
      { params },
    );
  },

  async fetchFacilityEnergyProfile(
    facilityId: number,
    params?: {
      start_date?: string;
      end_date?: string;
      granularity?: string;
    },
  ) {
    return await apiClient.get<FacilityEnergyProfileResponse>(
      `/api/v1/facilities/${facilityId}/energy-profile`,
      { params },
    );
  },

  async fetchResourceEnergyProfile(
    facilityId: number,
    resource_type: string,
    params?: {
      start_date?: string;
      end_date?: string;
      scope_id?: string;
      granularity?: string;
    },
  ) {
    return await apiClient.get<ResourceEnergyProfileResponse>(
      `/api/v1/facilities/${facilityId}/energy-profile/${resource_type}`,
      { params },
    );
  },

  async fetchSystemEnergyDistribution(
    facilityId: number,
    params?: {
      component_type?: string;
      resolution?: string;
      start_date?: string;
      end_date?: string;
    },
  ) {
    return await apiClient.get<SystemEnergyDistributionResponse>(
      `/api/v1/facilities/${facilityId}/energy-distribution`,
      { params },
    );
  },

  async fetchSystemEnergyPattern(
    facilityId: number,
    params?: {
      component_type?: string;
      start_date?: string;
      end_date?: string;
    },
  ) {
    return await apiClient.get<SystemEnergyPatternResponse>(
      `/api/v1/facilities/${facilityId}/energy-pattern`,
      { params },
    );
  },

  async fetchFacilityAiInsights(
    facilityId: number,
    params?: {
      strategy?: string;
    },
  ) {
    return await apiClient.get<AiInsightsResponse>(
      `/api/v1/facilities/${facilityId}/ai-insights`,
      { params },
    );
  },

  async createScenarios(facilityId: number, payload: CreateScenarioPayload) {
    return await apiClient.post<ScenarioResponse, CreateScenarioPayload>(
      `/api/v1/facilities/${facilityId}/scenarios`,
      payload,
    );
  },

  async createPerformanceAnalysis(
    facilityId: number,
    payload: PerformanceAnalysisPayload,
  ) {
    return await apiClient.post<
      PerformanceAnalysisResponse,
      PerformanceAnalysisPayload
    >(`/api/v1/facilities/${facilityId}/performance-analysis`, payload);
  },

  async fetchAvailableFacilityComponents(facilityId: number) {
    return await apiClient.get<SystemComponentTypesResponse>(
      `/api/v1/facilities/${facilityId}/available-components`,
    );
  },

  /**
   * Fetches the full summary for a single facility — asset portfolio,
   * telemetry, controls, team, and onboarding completion percentage.
   * Used by the Facility Summary page.
   */
  async fetchFacilitySummary(
    facilityId: number,
  ): Promise<FacilitySummaryResponse> {
    return apiClient.get(`/api/v1/facilities/${facilityId}/onboarding-summary`);
  },

  async fetchAdvancedEnergyAnalysis(
    facilityId: number,
    params: {
      start_datetime: string;
      end_datetime: string;
    },
  ) {
    return await apiClient.get<AdvancedEnergyAnalysisResponse>(
      `/api/v1/facilities/${facilityId}/advanced-energy-analysis`,
      { params },
    );
  },

  async exportFacilityExcel(
    facilityId: number,
    payload: ExportExcelPayload,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await api.post(
      `/api/v1/facilities/${facilityId}/export/excel`,
      payload,
      { responseType: "blob" },
    );
    const disposition: string = response.headers["content-disposition"] ?? "";
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiMatch = disposition.match(/filename="?([^";\n]+)"?/);
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T]/g, "")
      .slice(0, 15);
    const filename = utf8Match
      ? decodeURIComponent(utf8Match[1])
      : asciiMatch?.[1] ?? `export_${facilityId}_${timestamp}.xlsx`;
    return { blob: response.data as Blob, filename };
  },
};
